import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { LoaderCircle } from 'lucide-react';
import { SpendingSplitData } from '../../../utils/chartsUtils';
import { getColorPalette } from '../../../utils/chartColors';

ChartJS.register(ArcElement, Tooltip, Legend);

interface SpendingSplitChartProps {
  data: SpendingSplitData[];
  loading?: boolean;
}

export const SpendingSplitChart: React.FC<SpendingSplitChartProps> = ({ data, loading }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-card-dark p-6 rounded-[12px] relative h-full shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoaderCircle className="h-8 w-8 animate-spin text-textPrimary dark:text-textPrimary-dark" />
          </div>
        ) : (
          <p className="text-[#475467] dark:text-textSecondary-dark text-center py-8">{t('charts.noData')}</p>
        )}
      </div>
    );
  }

  const colors = getColorPalette(data.length);
  const chartData: ChartData<'doughnut'> = {
    labels: data.map(item => item.name),
    datasets: [
      {
        data: data.map(item => item.percentage),
        backgroundColor: colors,
        borderWidth: 0,
        borderRadius: 3,
        spacing: 2,
        hoverOffset: 0,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    cutout: '65%',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      }
    },
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `€${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
    }
    return `€${value.toFixed(2)}`;
  };

  const LegendItem = ({ item, index }: { item: SpendingSplitData; index: number }) => (
    <div className="flex items-center justify-between w-full mb-1.5 last:mb-0">
      <div className="flex items-center gap-1">
        <div
          className="w-1 h-3 rounded-full"
          style={{ backgroundColor: colors[index] }}
        />
        <span className="text-[#0C111D] dark:text-textPrimary-dark text-[14px] leading-6 font-semibold">
          {item.name} <span className="text-[#667085] dark:text-textQuaternary-dark ml-1.5">{item.percentage.toFixed(1)}%</span>
        </span>
      </div>
      <span className="text-[#101828] dark:text-textPrimary-dark text-[14px] leading-6 font-semibold">
        {formatCurrency(item.value)}
      </span>
    </div>
  );

  return (
    <>
      <div className="bg-white dark:bg-card-dark p-6 rounded-[12px] relative h-full shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
        <div className='mb-4 w-full flex items-center justify-between gap-4'>
          <div>
            <h1 className='text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1'>{t('charts.spendingSplitTitle')}</h1>
            <p className='text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal'>{t('charts.spendingSplitDescription')}</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className='border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 hidden md:flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300'
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
              <path d="M5.83317 14.1667H8.33317C8.56928 14.1667 8.7672 14.2466 8.92692 14.4063C9.08664 14.5661 9.1665 14.764 9.1665 15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334H4.99984C4.76373 15.8334 4.56581 15.7536 4.40609 15.5938C4.24637 15.4341 4.1665 15.2362 4.1665 15.0001V11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334C5.23595 10.8334 5.43387 10.9133 5.59359 11.073C5.75331 11.2327 5.83317 11.4306 5.83317 11.6667V14.1667ZM14.1665 5.83341H11.6665C11.4304 5.83341 11.2325 5.75355 11.0728 5.59383C10.913 5.43411 10.8332 5.23619 10.8332 5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675H14.9998C15.2359 4.16675 15.4339 4.24661 15.5936 4.40633C15.7533 4.56605 15.8332 4.76397 15.8332 5.00008V8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675C14.7637 9.16675 14.5658 9.08689 14.4061 8.92717C14.2464 8.76744 14.1665 8.56953 14.1665 8.33342V5.83341Z" fill="currentColor" />
            </svg>
            <span className='text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold'>Expand</span>
          </button>
        </div>

        <div className="flex items-center xl:flex-row flex-col gap-10">
          <div className="w-[230px] h-[230px] relative flex-shrink-0">
            <Doughnut data={chartData} options={options} />
          </div>
          <div className="w-full flex-1 flex flex-col justify-center">
            {data.map((item, index) => (
              <LegendItem key={index} item={item} index={index} />
            ))}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#16192466] dark:bg-[#16192466]" onClick={() => setIsModalOpen(false)}>
          <div className="bg-white dark:bg-card-dark rounded-xl w-full max-w-[850px] p-6 relative shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1">{t('charts.spendingSplitTitle')}</h2>
                <p className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal">{t('charts.spendingSplitDescription')}</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 px-3 flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
                  <path d="M7.49984 12.5001H4.99984C4.76373 12.5001 4.56581 12.4202 4.40609 12.2605C4.24637 12.1008 4.1665 11.9029 4.1665 11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334H8.33317C8.56928 10.8334 8.7672 10.9133 8.92692 11.073C9.08664 11.2327 9.1665 11.4306 9.1665 11.6667V15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334C8.09706 15.8334 7.89914 15.7536 7.73942 15.5938C7.5797 15.4341 7.49984 15.2362 7.49984 15.0001V12.5001ZM12.4998 7.50008H14.9998C15.2359 7.50008 15.4339 7.57994 15.5936 7.73966C15.7533 7.89939 15.8332 8.0973 15.8332 8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675H11.6665C11.4304 9.16675 11.2325 9.08689 11.0728 8.92717C10.913 8.76744 10.8332 8.56953 10.8332 8.33342V5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675C11.9026 4.16675 12.1005 4.24661 12.2603 4.40633C12.42 4.56605 12.4998 4.76397 12.4998 5.00008V7.50008Z" fill="currentColor" />
                </svg>
                <span className="text-[#667085] dark:text-textQuaternary-dark text-sm font-semibold">Collapse</span>
              </button>
            </div>
            <div className="flex items-center gap-12 justify-center px-8 pb-4">
              <div className="w-[300px] h-[300px] relative flex-shrink-0">
                <Doughnut data={chartData} options={options} />
              </div>
              <div className="w-[350px] flex flex-col justify-center">
                {data.map((item, index) => (
                  <LegendItem key={index} item={item} index={index} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
